import { useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../../../app/hooks';
import { updatePreventiveMaintenanceRequest } from '../state';
import { updatePreventiveMaintenanceRequest as updateRTPreventiveMaintenanceRequest } from '../../recurringTasks/state';
import { PreventiveMaintenanceRequestView } from '../../../types/shared/accessRequestTypes';
import { EditorMode } from '../types/editorTypes';

/**
 * Custom hook that abstracts Redux state selection and dispatch for
 * PM Details editors. Returns pm details, asset info, and mode-aware
 * update functions for both ticket and recurringTask slices.
 */
export const usePMDetailsEditor = (mode: EditorMode) => {
  const dispatch = useAppDispatch();

  const preventiveMaintenanceRequest = useAppSelector(state =>
    mode === 'ticket'
      ? state.ticket.currentTicket?.preventiveMaintenanceRequest
      : state.recurringTask.currentRecurringTask?.preventiveMaintenanceRequest
  );

  const assetInfo = useAppSelector(state => {
    const pm = mode === 'ticket'
      ? state.ticket.currentTicket?.preventiveMaintenanceRequest
      : state.recurringTask.currentRecurringTask?.preventiveMaintenanceRequest;
    return {
      assetId: pm?.assetId,
      assetTag: pm?.assetTag,
      assetName: pm?.assetName,
    };
  });

  const pmCategory = useAppSelector(state => {
    const pm = mode === 'ticket'
      ? state.ticket.currentTicket?.preventiveMaintenanceRequest
      : state.recurringTask.currentRecurringTask?.preventiveMaintenanceRequest;
    return pm?.category;
  });

  const pmInstructions = useAppSelector(state => {
    const pm = mode === 'ticket'
      ? state.ticket.currentTicket?.preventiveMaintenanceRequest
      : state.recurringTask.currentRecurringTask?.preventiveMaintenanceRequest;
    return pm?.instructions;
  });

  const updatePMField = useCallback(
    (update: Partial<PreventiveMaintenanceRequestView>) => {
      if (mode === 'ticket') {
        dispatch(updatePreventiveMaintenanceRequest(update));
      } else {
        dispatch(updateRTPreventiveMaintenanceRequest(update));
      }
    },
    [dispatch, mode]
  );

  const updateAsset = useCallback(
    (update: { assetId?: string; assetTag?: string; assetName?: string }) => {
      if (mode === 'ticket') {
        dispatch(updatePreventiveMaintenanceRequest(update));
      } else {
        dispatch(updateRTPreventiveMaintenanceRequest(update));
      }
    },
    [dispatch, mode]
  );

  return { pmDetails: preventiveMaintenanceRequest, assetInfo, pmCategory, pmInstructions, updatePMField, updateAsset };
};
